import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import GlobalHeader from './GlobalHeader';
import CalibrationModal from './CalibrationModal';
import MeetingNotesModal from './MeetingNotesModal';
import EditAssessmentModal from './EditAssessmentModal';
import MOMGeneratorModal from './MOMGeneratorModalFull';
import Snackbar from './Snackbar';
import './CalibrationReview.css';
 
// SVG Icons
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const CrossIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

// Get the appropriate icon based on status
const getStatusIcon = (status) => {
  switch (status) {
    case 'Aligned':
      return <CheckIcon />;
    case 'Minor Gap':
      return <WarningIcon />;
    case 'Review':
      return <CrossIcon />;
    default:
      return <CheckIcon />;
  }
};
 
function CalibrationReview() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('All');
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [managerFilter, setManagerFilter] = useState('Employee Assessment');

  // Reset selected manager when filter changes
  const handleManagerFilterChange = (value) => {
    setManagerFilter(value);
    if (value !== 'Employee Assessment') {
      setSelectedManager(null);
    }
  };
  const [tabs, setTabs] = useState([
    { id: 'All', label: 'All', count: 0 },
    { id: 'Aligned', label: 'Aligned', count: 0 },
    { id: 'Minor Gap', label: 'Minor Gap', count: 0 },
    { id: 'Review', label: 'Review', count: 0 },
  ]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  // Meeting Notes Modal state
  const [isMeetingNotesModalOpen, setIsMeetingNotesModalOpen] = useState(false);
  const [meetingNotesData, setMeetingNotesData] = useState(null);
  const [currentMeetingNotesAssessment, setCurrentMeetingNotesAssessment] = useState(null);

  // Edit Assessment Modal state
  const [isEditAssessmentModalOpen, setIsEditAssessmentModalOpen] = useState(false);
  const [editAssessmentData, setEditAssessmentData] = useState(null);

  // MOM Generator Modal state
  const [isMOMGeneratorModalOpen, setIsMOMGeneratorModalOpen] = useState(false);
  const [currentMOMAssessment, setCurrentMOMAssessment] = useState(null);

  // Cycle filter state
  const [availableCycles, setAvailableCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);

  // Manager dropdown state
  const [availableManagers, setAvailableManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // Department filter state for admin
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });

 
  useEffect(() => {
    document.title = 'CR Capability Index - Calibration Review';
  }, []);

  // Fetch available cycles on component mount
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const cyclesResponse = await apiService.fetchData('metadata?type=cycles');
        if (cyclesResponse?.status === 'success' && cyclesResponse.data.length > 0) {
          // Sort cycles by CycleID DESC to get latest first
          const sortedCycles = [...cyclesResponse.data].sort((a, b) => (b.CycleID || 0) - (a.CycleID || 0));
          setAvailableCycles(sortedCycles);
          // Set latest cycle as default
          setSelectedCycle(sortedCycles[0]);
        }
      } catch (err) {
        console.error('Error fetching cycles:', err);
      }
    };

    fetchCycles();
  }, []);

  // Fetch managers under current manager (only for managers)
  useEffect(() => {
    if (!selectedCycle) return; // Wait for cycle to be selected

    const fetchManagers = async () => {
      try {
        const role = sessionStorage.getItem('access_role') || 'user';
        const userEmail = sessionStorage.getItem('user_emailId');

        if (role === 'manager' && userEmail) {
          setLoadingManagers(true);
          const managersResponse = await apiService.postData("/get-manager", { 
            manager_emails: [userEmail],
            cycle_id: selectedCycle.CycleID
          });
          if (managersResponse?.status === 'success' && managersResponse.data && managersResponse.data.length > 0) {
            // Filter only managers (those with is_manager: true) from the employees array
            const managersOnly = managersResponse.data[0].employees.filter(employee => employee.is_manager === true);
            setAvailableManagers(managersOnly);
          }
        }
      } catch (err) {
        console.error('Error fetching managers:', err);
      } finally {
        setLoadingManagers(false);
      }
    };

    fetchManagers();
  }, [selectedCycle]);

  // Fetch departments (for admin)
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const role = sessionStorage.getItem('access_role') || 'user';

        if (role === 'admin') {
          setLoadingDepartments(true);

          // Fetch departments
          const departmentsResponse = await apiService.fetchData('metadata?type=departments');
          if (departmentsResponse?.status === 'success' && departmentsResponse.data) {
            setAvailableDepartments(departmentsResponse.data);
          }
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, []);

  // Fetch managers based on selected department (for admin)
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const role = sessionStorage.getItem('access_role') || 'user';

        if (role === 'admin') {
          setLoadingManagers(true);

          // Build API URL with department filter if selected
          let apiUrl = 'get-all-managers';
          if (selectedDepartment) {
            apiUrl += `?department_name=${encodeURIComponent(selectedDepartment)}`;
          }

          const managersResponse = await apiService.fetchData(apiUrl);
          if (managersResponse?.status === 'success' && managersResponse.data) {
            setAvailableManagers(managersResponse.data);
          }
        }
      } catch (err) {
        console.error('Error fetching managers:', err);
      } finally {
        setLoadingManagers(false);
      }
    };

    fetchManagers();
  }, [selectedDepartment]);

  // Fetch assessments based on user role, cycle, and manager filter
  useEffect(() => {
    if (!selectedCycle) return; // Wait for cycle to be selected

    const fetchAssessments = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get user role from session storage
        const role = sessionStorage.getItem('access_role') || 'user';
        const userEmail = sessionStorage.getItem('user_emailId');

        setUserRole(role);

        console.log('User role:', role);
        console.log('User email:', userEmail);
        console.log('Selected cycle:', selectedCycle);

        let apiUrl = '';
        const cycleParam = `&cycle_id=${selectedCycle.CycleID}`;

        if (role === 'admin') {
          // For admin, check if a specific manager is selected
          if (selectedManager) {
            // If manager is selected, get reviews for employees under that manager
            apiUrl = `employee-reviews?type=manager&email=${encodeURIComponent(selectedManager)}${cycleParam}`;
          } else {
            // If no manager selected, get all employee reviews
            apiUrl = `employee-reviews?type=admin${cycleParam}`;

            // Add department filter if selected
            if (selectedDepartment) {
              apiUrl += `&department=${encodeURIComponent(selectedDepartment)}`;
            }
          }

        } else if (role === 'manager') {
          // For managers, check the filter and call appropriate API
          if (managerFilter === 'Employee Assessment') {
            // Manager viewing employee assessments
            // If a specific manager is selected, show their subordinates; otherwise show direct reports
            const managerEmail = selectedManager ? selectedManager.Email : userEmail;
            apiUrl = `employee-reviews?type=manager&email=${managerEmail}${cycleParam}`;
          } else if (managerFilter === 'My Assessment') {
            // Manager viewing their own assessments
            apiUrl = `employee-reviews?type=employee&email=${userEmail}${cycleParam}`;
          }
        } else {
          // For employees (and default user role)
          apiUrl = `employee-reviews?type=employee&email=${userEmail}${cycleParam}`;
        }

        console.log('Calling API:', apiUrl);

        const response = await apiService.fetchData(apiUrl);
        console.log('API Response:', response);

        if (response && response.status === 'success') {
          // Process the response data into assessment cards
          const processedAssessments = processApiResponse(response.data || []);
          setAssessments(processedAssessments);

          // Note: Admin manager filter now uses availableManagers from /get-all-managers API

          // Update tab counts
          const newTabs = calculateTabCounts(processedAssessments);
          setTabs(newTabs);

          // Handle empty state gracefully
          if (processedAssessments.length === 0) {
            console.log('No assessments found for the current user');
          }
        } else {
          throw new Error(response?.message || 'Failed to fetch assessments');
        }

      } catch (err) {
        console.error('Error fetching assessments:', err);
        setError(err.message || 'Failed to load assessments');
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [selectedCycle, managerFilter, selectedManager, selectedDepartment]);

 
  const filteredAssessments = activeTab === 'All' ? assessments : assessments.filter(a => a.status === activeTab);
 
  // Process API response into assessment cards
  const processApiResponse = (data) => {
    const assessments = [];

    data.forEach(cycle => {
      cycle.employees.forEach(employee => {
        // Get reviews array for backward compatibility
        const reviews = employee.reviews;

        // Use API-provided weighted averages instead of manual calculation
        const avgSelfScore = employee.self_average_rating ? parseFloat(employee.self_average_rating).toFixed(2) : null;
        const avgManagerScore = employee.manager_average_rating ? parseFloat(employee.manager_average_rating).toFixed(2) : null;

        // Calculate discrepancy (manager-assessment - self-assessment)
        // Positive = manager rated higher, Negative = employee rated higher
        const discrepancy = avgSelfScore && avgManagerScore ? (parseFloat(avgManagerScore) - parseFloat(avgSelfScore)).toFixed(2) : null;

        // Determine status based on discrepancy value
        let status = 'Incomplete';
        if (discrepancy !== null) {
          const discValue = parseFloat(discrepancy);
          if (discValue >= 0) {
            // Manager rated higher or equal - always Aligned
            status = 'Aligned';
          } else {
            // Employee rated higher - check negative discrepancy
            if (discValue > -0.3) {
              // Negative discrepancy > -0.3 (e.g., -0.2, -0.1) - still Aligned
              status = 'Aligned';
            } else if (discValue >= -0.6) {
              // -0.3 <= negative discrepancy < -0.6
              status = 'Minor Gap';
            } else {
              // negative discrepancy <= -0.6
              status = 'Review';
            }
          }
        }

        // Get submission dates
        const selfSubmittedAt = reviews.find(r => r.SelfSubmittedAt)?.SelfSubmittedAt;
        const managerSubmittedAt = reviews.find(r => r.ManagerSubmittedAt)?.ManagerSubmittedAt;

        const assessment = {
          id: `${cycle.cycle_id}-${employee.email}`, // Unique ID combining cycle and email
          reviewId: reviews[0]?.ReviewID || null, // Use first review ID for navigation
          name: employee.email ? employee.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown', // Format email as name
          email: employee.email,
          role: reviews[0]?.Role || 'Unknown Role',
          department: reviews[0]?.DepartmentName || 'Unknown Department',
          cycleId: cycle.cycle_id,
          selfScore: avgSelfScore,
          managerScore: avgManagerScore,
          discrepancy: discrepancy,
          status: status,
          selfDate: selfSubmittedAt ? new Date(selfSubmittedAt).toLocaleDateString() : 'Not submitted',
          managerDate: managerSubmittedAt ? new Date(managerSubmittedAt).toLocaleDateString() : 'Not submitted',
          reviews: reviews, // Store all reviews for detailed view
          is_scheduled: employee.is_scheduled || false, // Track if meeting is already scheduled
          is_published: employee.is_published || false, // Track if manager assessment is published
          is_meeting_notes_available: employee.is_meeting_notes_available || 0, // Track if AI processed meeting notes are available
          is_manager_assessment_edited: employee.is_manager_assessment_edited || false // Track if manager assessment has been edited after scheduling
        };

        assessments.push(assessment);
      });
    });

    console.log('Processed assessments:', assessments);
    return assessments;
  };

  // Calculate tab counts
  const calculateTabCounts = (assessments) => {
    const counts = {
      All: assessments.length,
      Aligned: assessments.filter(a => a.status === 'Aligned').length,
      'Minor Gap': assessments.filter(a => a.status === 'Minor Gap').length,
      Review: assessments.filter(a => a.status === 'Review').length
    };

    return [
      { id: 'All', label: 'All', count: counts.All },
      { id: 'Aligned', label: 'Aligned', count: counts.Aligned },
      { id: 'Minor Gap', label: 'Minor Gap', count: counts['Minor Gap'] },
      { id: 'Review', label: 'Review', count: counts.Review },
    ];
  };
 
  const handleViewDetails = (assessment) => {
    // Open modal with assessment details
    setSelectedAssessment(assessment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAssessment(null);
  };

  const handleCloseMeetingNotesModal = () => {
    setIsMeetingNotesModalOpen(false);
    setMeetingNotesData(null);
    setCurrentMeetingNotesAssessment(null);
  };

  const handleEditAssessment = (assessment) => {
    setEditAssessmentData(assessment);
    setIsEditAssessmentModalOpen(true);
  };

  const handleCloseEditAssessmentModal = () => {
    setIsEditAssessmentModalOpen(false);
    setEditAssessmentData(null);
  };

  const handleSaveEditedAssessment = async (updatedAssessment) => {
    try {
      // Call API to save the edited assessment
      const response = await apiService.postData('/edit-manager-assessment', {
        cycle_id: updatedAssessment.cycleId,
        employee_email: updatedAssessment.email,
        manager_email: sessionStorage.getItem('user_emailId'),
        updated_reviews: updatedAssessment.reviews
      });

      if (response.status === 'success') {
        // Refresh the assessments data
        // This will trigger a re-fetch of the data
        const cycleId = selectedCycle?.CycleID;
        if (cycleId) {
          setSelectedCycle({ ...selectedCycle }); // Trigger re-fetch
        }

        // Close the modal
        handleCloseEditAssessmentModal();
      } else {
        throw new Error(response.message || 'Failed to save edited assessment');
      }
    } catch (error) {
      console.error('Error saving edited assessment:', error);
      // You might want to show an error message to the user here
      alert('Failed to save edited assessment. Please try again.');
    }
  };

  const handleCycleChange = (event) => {
    const cycleId = parseInt(event.target.value);
    const selected = availableCycles.find(cycle => cycle.CycleID === cycleId);
    setSelectedCycle(selected);
  };
 
  const handleScheduleReview = (assessment) => {
    const personName = assessment.name.replace(/\s+/g, '-').toLowerCase();
    navigate(`/schedulereview/${personName}`, { state: { assessment } });
  };

  const handleViewMeetingNotes = async (assessment) => {
    try {
      setLoading(true);
      const response = await apiService.postData('/meeting-notes', {
        cycle_id: assessment.cycleId,
        employee_email: assessment.email,
        manager_email: sessionStorage.getItem('user_emailId')
      });

      if (response.status === 'success' || response.status === 'pending') {
        // Show meeting notes in modal
        setMeetingNotesData({
          notes: response.data.meeting_notes,
          date: response.data.meeting_datetime,
          employeeName: assessment.name,
          meetingLink: response.data.meeting_link,
          scheduledBy: response.data.scheduled_by,
          source: response.data.source
        });
        setCurrentMeetingNotesAssessment(assessment);
        setIsMeetingNotesModalOpen(true);
      } else {
        // Show message in modal for other statuses (not_found, not_scheduled, error)
        setMeetingNotesData({
          notes: response.message,
          date: null,
          employeeName: assessment.name,
          meetingLink: null,
          scheduledBy: null,
          source: null
        });
        setCurrentMeetingNotesAssessment(assessment);
        setIsMeetingNotesModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching meeting notes:', error);
      setMeetingNotesData({
        notes: 'Error retrieving meeting notes. Please try again.',
        date: null,
        employeeName: assessment.name,
        meetingLink: null,
        scheduledBy: null,
        source: null
      });
      setCurrentMeetingNotesAssessment(assessment);
      setIsMeetingNotesModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMeetingNotes = async (cycleId, employeeEmail, managerEmail, notes) => {
    try {
      setLoading(true);
      const response = await apiService.putData('/meeting-notes', {
        cycle_id: cycleId,
        employee_email: employeeEmail,
        manager_email: managerEmail,
        meeting_notes: notes
      });

      if (response.status === 'success') {
        // Update the local state to reflect the saved notes
        setMeetingNotesData(prev => ({
          ...prev,
          notes: notes
        }));
        setSnackbar({
          show: true,
          message: 'Meeting notes saved successfully!',
          type: 'success'
        });
      } else {
        throw new Error(response.message || 'Failed to save meeting notes');
      }
    } catch (error) {
      console.error('Error saving meeting notes:', error);
      setSnackbar({
        show: true,
        message: 'Failed to save meeting notes. Please try again.',
        type: 'error'
      });
      throw error; // Re-throw to let the modal handle the error
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMOM = (assessment) => {
    setCurrentMOMAssessment(assessment);
    setIsMOMGeneratorModalOpen(true);
  };

  const handleCloseMOMGeneratorModal = () => {
    setIsMOMGeneratorModalOpen(false);
    setCurrentMOMAssessment(null);
  };

  return (
    <div className="calibration-review-page">
      <GlobalHeader />
 
      {/* Header */}
      <div className="ma-page-header">
        <button className="ma-back-button" onClick={() => navigate('/Home')}>
          <span className="back-icon">←</span> Back to Home
        </button>
        <div className="ma-header-content">
          <div className="ma-heading">
            <h1 className="page-title">Calibration Review</h1>
            <p className="page-subtitle">Compare self-assessment vs manager ratings and identify discrepancies</p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        {/* Cycle Filter */}
        <div className="filter-item">
          <label>Assessment Cycle:</label>
          <select
            value={selectedCycle?.CycleID || ''}
            onChange={handleCycleChange}
            disabled={loading || availableCycles.length === 0}
          >
            {availableCycles.map((cycle) => (
              <option key={cycle.CycleID} value={cycle.CycleID}>
                {cycle.Name || cycle.CycleID}
              </option>
            ))}
          </select>
        </div>

        {/* Department Filter for Admin */}
        {userRole === 'admin' && availableDepartments.length > 0 && (
          <div className="filter-item">
            <label>Department:</label>
            <select
              value={selectedDepartment || ''}
              onChange={(e) => setSelectedDepartment(e.target.value || null)}
              disabled={loadingDepartments}
            >
              <option value="">All Departments</option>
              {availableDepartments.map((dept) => (
                <option key={dept.DepartmentID || dept.DepartmentName} value={dept.DepartmentName}>
                  {dept.DepartmentName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Manager Filter for Admin */}
        {userRole === 'admin' && availableManagers.length > 0 && (
          <div className="filter-item">
            <label>Manager:</label>
            <select
              value={selectedManager || ''}
              onChange={(e) => {
                const managerEmail = e.target.value;
                setSelectedManager(managerEmail || null);
              }}
              disabled={loadingManagers}
            >
              <option value="">All Managers</option>
              {availableManagers.map((manager) => (
                <option key={manager} value={manager}>
                  {manager}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Manager Filter Dropdown */}
        {userRole === 'manager' && (
          <>
            <div className="filter-item">
              <label>View:</label>
            <select
              value={managerFilter}
              onChange={(e) => handleManagerFilterChange(e.target.value)}
            >
                <option value="Employee Assessment">Employee Assessment</option>
                <option value="My Assessment">My Assessment</option>
              </select>
            </div>

            {/* Manager Review Dropdown */}
            {managerFilter === 'Employee Assessment' && availableManagers.length > 0 && (
              <div className="filter-item">
                <label>Manager Review:</label>
                <select
                  value={selectedManager?.Email || ''}
                  onChange={(e) => {
                    const managerEmail = e.target.value;
                    const manager = availableManagers.find(m => m.Email === managerEmail);
                    setSelectedManager(manager || null);
                  }}
                  disabled={loadingManagers}
                >
                  <option value="">All Direct Reports</option>
                  {availableManagers.map((manager) => (
                    <option key={manager.ID} value={manager.Email}>
                      {manager.FullName} {manager.Email ? `(${manager.Email.split('@')[0]})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <p>Loading assessments...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-container">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {/* Show content only when not loading and no error */}
      {!loading && !error && (
        <>
      {/* Summary Cards - Tall, Rounded, Bordered */}
      <div className="summary-cards">
        <div className="summary-card aligned">
          <div className="card-title">Aligned</div>
              <div className="card-value"><CheckIcon /> {tabs.find(t => t.id === 'Aligned')?.count || 0}</div>
          <div className="card-note">Manager ≥ Employee OR Self &gt; Manager ≤ 0.3</div>
        </div>
        <div className="summary-card minor-gap">
          <div className="card-title">Minor Gap</div>
              <div className="card-value"><WarningIcon /> {tabs.find(t => t.id === 'Minor Gap')?.count || 0}</div>
          <div className="card-note">Self &gt; Manager by 0.3 - 0.6</div>
        </div>
        <div className="summary-card needs-review">
          <div className="card-title">Needs Review</div>
              <div className="card-value"><CrossIcon /> {tabs.find(t => t.id === 'Review')?.count || 0}</div>
          <div className="card-note">Self &gt; Manager by &gt; 0.6</div>
        </div>
      </div>
 
      {/* Assessment Calibration Section */}
      <div className="calibration-section">
        <div className="section-header">
          <h2>Assessment Calibration</h2>
          <p>Review and calibrate self-assessment vs manager assessment discrepancies.</p>
        </div>
 
        {/* Tabs - Full Width */}
        <div className="tabs-bar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
 
        {/* Cards Grid - 3 per row */}
        <div className="cards-grid">
          {filteredAssessments.length === 0 ? (
            <div className="no-assessments-message">
              <div className="no-assessments-content">
                <div className="no-assessments-icon">📋</div>
                <h3>No Assessments Found</h3>
                <p>
                  {userRole === 'manager' && managerFilter === 'My Assessment'
                    ? "You haven't submitted any self-assessments yet."
                    : userRole === 'manager' && managerFilter === 'Employee Assessment'
                    ? "No employee assessments are available for review."
                    : "No assessments are available for your review."}
                </p>
                <p className="suggestion-text">
                  {userRole === 'manager'
                    ? "Try switching between 'My Assessment' and 'Employee Assessment' views."
                    : "Contact your manager or HR if you believe this is an error."}
                </p>
              </div>
            </div>
          ) : (
            filteredAssessments.map(ass => (
            <div key={ass.id} className="assessment-card" onClick={() => handleViewDetails(ass)}>
              <div className="card-top">
                <div className="name-role">
                  <h3 className="name">{ass.name}</h3>
                  <p className="role">{ass.role}</p>
                </div>
                  <span className={`status-badge ${ass.status.toLowerCase().replace(' ', '-')}`}>{ass.status}</span>
              </div>
 
              <div className="score-row">
                <div className="score-box self">
                  <div className="score-label">Self Assessment</div>
                  <div className="score-value">{ass.selfScore}</div>
                  <div className="score-date">{ass.selfDate}</div>
                </div>
                <div className="score-box manager">
                  <div className="score-label">Manager Assessment</div>
                  <div className="score-value">
                    {(() => {
                      // For users and manager's "My Assessment" view, show different text based on publish status
                      const shouldHideScore = (userRole === 'user' || (userRole === 'manager' && managerFilter === 'My Assessment')) && !ass.is_published;
                      const hasManagerRating = ass.managerScore !== null && ass.managerScore !== undefined;

                      if (shouldHideScore) {
                        // Show "Results not published" if manager rating exists but not published
                        // Show "Not submitted" if no manager rating exists
                        return hasManagerRating ? 'Results not published' : 'Not submitted';
                      }
                      return ass.managerScore || 'Not submitted';
                    })()}
                  </div>
                  <div className="score-date">{ass.managerDate}</div>
                </div>
              </div>
 
              <div className="discrepancy-row">
                  <div className="discrepancy-icon">{getStatusIcon(ass.status)}</div>
                <div className="discrepancy-text">
                  <div className="discrepancy-label">Discrepancy</div>
                  <div className="discrepancy-value">
                    {(() => {
                      // For users and manager's "My Assessment" view, hide discrepancy if not published
                      const shouldHideScore = (userRole === 'user' || (userRole === 'manager' && managerFilter === 'My Assessment')) && !ass.is_published;
                      if (shouldHideScore) {
                        return 'N/A';
                      }
                      return ass.discrepancy || 'N/A';
                    })()}
                  </div>
                </div>
              </div>
 
              <div className="action-row">
                <button className="btn view-details" onClick={(e) => { e.stopPropagation(); handleViewDetails(ass); }}>
                  View Details
                </button>
                {(userRole === 'admin' || userRole === 'manager') && (
                  <>
                    {/* Debug log for button logic */}
                    {console.log('Button logic debug:', {
                      is_scheduled: ass.is_scheduled,
                      is_meeting_notes_available: ass.is_meeting_notes_available,
                      userRole: userRole,
                      managerFilter: managerFilter,
                      assessmentName: ass.name
                    })}
                    {/* Schedule Review / View Meeting Notes Button */}
                    {!ass.is_scheduled ? (
                      <button
                        className={`btn schedule-review ${userRole === 'manager' && managerFilter === 'My Assessment' ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!(userRole === 'manager' && managerFilter === 'My Assessment')) {
                            handleScheduleReview(ass);
                          }
                        }}
                        disabled={userRole === 'manager' && managerFilter === 'My Assessment'}
                        title={
                          userRole === 'manager' && managerFilter === 'My Assessment'
                            ? 'Schedule Review is not available for your own assessment'
                            : 'Schedule Review Meeting'
                        }
                      >
                        Schedule Review
                      </button>
                    ) : ass.is_meeting_notes_available === 1 ? (
                      <button
                        className="btn schedule-review view-notes"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewMeetingNotes(ass);
                        }}
                        title="View Meeting Notes"
                      >
                        View Meeting Notes
                      </button>
                    ) : (
                      <button
                        className="btn schedule-review generate-mom"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateMOM(ass);
                        }}
                        title="Generate Minutes of Meeting"
                      >
                        Generate MOM
                      </button>
                    )}
                  </>
                )}
                {/* Edit Assessment button - only for managers, only when meeting is scheduled, and only if not already edited */}
                {userRole === 'manager' && managerFilter === 'Employee Assessment' && ass.is_scheduled && !ass.is_manager_assessment_edited && (
                  <button
                    className="btn edit-assessment"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAssessment(ass);
                    }}
                    title="Edit Manager Assessment (available only once after meeting scheduling)"
                  >
                    Edit Assessment
                  </button>
                )}
              </div>
            </div>
            ))
          )}
        </div>
      </div>
        </>
      )}
 
 
      {/* Calibration Modal */}
      {isModalOpen && selectedAssessment && (
        <CalibrationModal
          assessment={{
            ...selectedAssessment,
            managerFilter: managerFilter // Pass manager filter to modal
          }}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}

      {/* Meeting Notes Modal */}
      {isMeetingNotesModalOpen && meetingNotesData && currentMeetingNotesAssessment && (
        <MeetingNotesModal
          isOpen={isMeetingNotesModalOpen}
          onClose={handleCloseMeetingNotesModal}
          meetingNotes={meetingNotesData.notes}
          meetingDate={meetingNotesData.date}
          employeeName={meetingNotesData.employeeName}
          meetingLink={meetingNotesData.meetingLink}
          scheduledBy={meetingNotesData.scheduledBy}
          cycleId={currentMeetingNotesAssessment.cycleId}
          employeeEmail={currentMeetingNotesAssessment.email}
          managerEmail={sessionStorage.getItem('user_emailId')}
          onSave={handleSaveMeetingNotes}
        />
      )}

      {/* Edit Assessment Modal */}
      {isEditAssessmentModalOpen && editAssessmentData && (
        <EditAssessmentModal
          assessment={editAssessmentData}
          isOpen={isEditAssessmentModalOpen}
          onClose={handleCloseEditAssessmentModal}
          onSave={handleSaveEditedAssessment}
        />
      )}

      {/* MOM Generator Modal */}
      {isMOMGeneratorModalOpen && currentMOMAssessment && (
        <MOMGeneratorModal
          isOpen={isMOMGeneratorModalOpen}
          onClose={handleCloseMOMGeneratorModal}
          employeeName={currentMOMAssessment.name}
          cycleId={currentMOMAssessment.cycleId}
          employeeEmail={currentMOMAssessment.email}
          managerEmail={sessionStorage.getItem('user_emailId')}
        />
      )}

      {/* Snackbar notification */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          duration={3000}
          onClose={() => setSnackbar({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>
  );
}
 
export default CalibrationReview;